import { Link } from 'react-router-dom';
import { Video, Award, Users, Star } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { optimizeImageUrl } from '@/lib/imageUtils';

interface ChatCourseCardProps {
  product: {
    slug: string;
    title: string;
    subtitle?: string;
    image?: string;
    profile_image?: string;
    price?: number;
    msrp?: number;
    price_duration?: string;
    rating?: number;
    review_count?: number;
    instructor_info?: {
      name?: string;
      title?: string;
      avatar_url?: string;
    };
    total_video_hours?: number;
    certificate_included?: boolean;
    student_count?: number;
    skill_level?: string;
  };
}

export function ChatCourseCard({ product }: ChatCourseCardProps) {
  const displayPrice = product.price || 0;
  const formattedPrice = displayPrice === 0 ? 'Free' : `$${displayPrice}`;
  const discountPercent = product.msrp && product.msrp > displayPrice 
    ? Math.round(((product.msrp - displayPrice) / product.msrp) * 100) 
    : 0;

  const formatStudentCount = (count?: number) => {
    if (!count) return '0';
    if (count >= 1000) return `${(count / 1000).toFixed(1)}k`;
    return count.toString();
  };

  return (
    <Card className="overflow-hidden hover:shadow-lg transition-all">
      <CardContent className="p-0">
        <div className="flex gap-3 p-3">
          {/* Course Image with Instructor Avatar */}
          <div className="relative w-32 h-32 shrink-0 rounded-lg overflow-hidden bg-muted">
            {product.image ? (
              <img
                src={optimizeImageUrl(product.image, { width: 128, height: 128 })}
                alt={product.title}
                className="w-full h-full object-cover"
                loading="lazy"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-4xl">
                📚
              </div>
            )}
            
            {/* Instructor Avatar Overlay */}
            {(product.profile_image || product.instructor_info?.avatar_url) && (
              <div className="absolute -bottom-2 -right-2 w-10 h-10 rounded-full border-2 border-card overflow-hidden ring-2 ring-primary/20 shadow-md">
                <img
                  src={optimizeImageUrl(
                    product.profile_image || product.instructor_info?.avatar_url,
                    { width: 40, height: 40 }
                  )}
                  alt={product.instructor_info?.name || 'Instructor'}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            )}
            
            {/* COURSE Badge */}
            <Badge className="absolute top-2 left-2 bg-course-badge text-white font-semibold text-xs">
              COURSE
            </Badge>
          </div>

          {/* Course Details */}
          <div className="flex-1 min-w-0 flex flex-col">
            {/* Title */}
            <h4 className="font-bold text-sm leading-tight line-clamp-2 mb-1">
              {product.title}
            </h4>

            {/* Instructor */}
            {product.instructor_info?.name && (
              <p className="text-xs text-muted-foreground mb-2">
                by <span className="font-semibold text-foreground">{product.instructor_info.name}</span>
              </p>
            )}

            {/* Stats Row */}
            <div className="flex items-center gap-2 text-xs mb-2 flex-wrap">
              {/* Rating */}
              {product.rating && product.rating > 0 && (
                <div className="flex items-center gap-1 bg-amber-50 dark:bg-amber-950/20 px-1.5 py-0.5 rounded">
                  <Star className="w-3 h-3 fill-amber-500 text-amber-500" />
                  <span className="font-bold">{product.rating.toFixed(1)}</span>
                </div>
              )}

              {/* Students */}
              {product.student_count && product.student_count > 0 && (
                <div className="flex items-center gap-1 text-muted-foreground">
                  <Users className="w-3 h-3" />
                  <span className="font-medium">{formatStudentCount(product.student_count)}</span>
                </div>
              )}

              {/* Video Hours */}
              {product.total_video_hours && (
                <div className="flex items-center gap-1 text-muted-foreground">
                  <Video className="w-3 h-3" />
                  <span className="font-medium">{product.total_video_hours}hrs</span>
                </div>
              )}

              {/* Certificate */}
              {product.certificate_included && (
                <div className="flex items-center gap-1 text-course-accent">
                  <Award className="w-3 h-3" />
                  <span className="font-medium text-xs">Cert</span>
                </div>
              )}
            </div>

            {/* Skill Level */}
            {product.skill_level && (
              <Badge variant="secondary" className="text-xs w-fit mb-2">
                {product.skill_level}
              </Badge>
            )}

            {/* Spacer */}
            <div className="flex-1" />

            {/* Price & CTA */}
            <div className="flex items-center justify-between mt-auto">
              <div className="flex items-baseline gap-2">
                <span className="text-lg font-extrabold text-foreground">
                  {formattedPrice}
                </span>
                {discountPercent > 0 && (
                  <span className="text-xs text-muted-foreground line-through">
                    ${product.msrp}
                  </span>
                )}
              </div>
              <Link to={`/product/${product.slug}`}>
                <Button size="sm" className="h-8 text-xs font-bold">
                  Enroll Now
                </Button>
              </Link>
            </div>

            {discountPercent > 0 && (
              <span className="text-xs text-course-accent font-bold bg-course-accent/10 px-1.5 py-0.5 rounded mt-1 w-fit">
                {discountPercent}% OFF
              </span>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
